import React from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { languages, changeLanguage } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LanguageSelector = ({ variant = 'icon' }) => {
  const { i18n } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = async (langCode) => {
    // Immediately change the UI language
    changeLanguage(langCode);
    
    // If user is logged in, save preference to backend
    if (user) {
      try {
        await axios.put(`${API}/auth/me`, 
          { language: langCode },
          { headers: getAuthHeaders() }
        );
      } catch (error) {
        // Silent fail - language is already changed locally
        console.error('Failed to save language preference:', error);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'icon' ? (
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            data-testid="language-selector-btn"
          >
            <Globe className="h-5 w-5" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" data-testid="language-selector-btn">
            <Globe className="h-4 w-4 mr-2" />
            {currentLanguage.nativeName}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex justify-between ${lang.code === i18n.language ? 'bg-muted' : ''}`}
            data-testid={`lang-option-${lang.code}`}
          >
            <span>{lang.nativeName}</span>
            {lang.code === i18n.language && (
              <span className="text-primary">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
